import { Box, IconButton, Typography, useTheme } from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import { Maybe } from "../../models/common/generic-types";
import { firstLetterUppercase } from "../../utils/first-letter-uppercase";
import { useStyles } from "../date-range-picker/styles";

interface YearProps {
	year: number;
	currentYear?: number;
	startDate?: Maybe<Date>;
	endDate?: Maybe<Date>;
	goToPreviousYear: () => void;
	goToNextYear: () => void;
	handleSelectMonth: (date: Date) => void;
	previousDisabled?: boolean;
	nextDisabled?: boolean;
	minDate?: Date;
	maxDate?: Date;
}

const Year: React.FC<YearProps> = ({
	currentYear,
	year,
	startDate,
	endDate,
	goToPreviousYear,
	previousDisabled,
	goToNextYear,
	nextDisabled,
	handleSelectMonth,
	minDate,
	maxDate,
}) => {
	const { spacing } = useTheme();

	const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

	return (
		<Box>
			<Box display="flex" justifyContent="space-between">
				<IconButton onClick={goToPreviousYear} disabled={!!previousDisabled}>
					<ChevronLeft />
				</IconButton>
				<Typography
					style={{
						paddingTop: spacing(1.6),
						fontWeight: 600,
						color: "black",
					}}
				>
					{year}
				</Typography>
				<IconButton onClick={goToNextYear} disabled={!!nextDisabled}>
					<ChevronRight />
				</IconButton>
			</Box>
			<Box display="grid" gridTemplateColumns="repeat(4, 1fr)" justifyContent="center">
				{months.map((month, index) => {
					return (
						<Box key={index} bgcolor={"white"}>
							<Month
								monthLabel={month}
								handleSelectMonth={handleSelectMonth}
								monthIndex={index}
								year={year}
								currentYear={currentYear || year}
								startDate={startDate}
								endDate={endDate}
								minDate={minDate}
								maxDate={maxDate}
							/>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};

export default Year;

interface IMonth {
	monthLabel: string;
	handleSelectMonth: (date: Date) => void;
	monthIndex: number;
	year: number;
	currentYear: number;
	startDate?: Maybe<Date>;
	endDate?: Maybe<Date>;
	minDate?: Date;
	maxDate?: Date;
}

const Month = ({ monthLabel, handleSelectMonth, monthIndex, year, currentYear, startDate, endDate, minDate, maxDate }: IMonth) => {
	const { spacing, palette } = useTheme();
	const selectDate = (month: number, year: number) => {
		const newDate = new Date(year, month, 1);
		handleSelectMonth(newDate);
	};
	const { monthDatePicker, monthText } = useStyles();

	const isMaxDisabled = maxDate && year >= maxDate.getFullYear() && monthIndex > maxDate.getMonth();
	const isMinDisabled = minDate && year <= minDate.getFullYear() && monthIndex < minDate.getMonth();
	const isMonthDisabled = isMaxDisabled || isMinDisabled;
	const appliedClass = isMonthDisabled ? "" : monthDatePicker;

	const isSelected =
		(!!startDate || !!endDate) &&
		((startDate?.getFullYear() === currentYear && startDate.getMonth() === monthIndex) ||
			(endDate?.getFullYear() === currentYear && endDate.getMonth() === monthIndex));

	return (
		<Box
			style={{
				justifyContent: "center",
				alignItems: "center",
				display: "flex",
				padding: "0 2px",
				marginBottom: spacing(0.5),
				backgroundColor: isSelected ? "#367bf5" : "",
				borderRadius: "64px",
			}}
			className={appliedClass}
		>
			<IconButton
				className={appliedClass}
				onClick={() => selectDate(monthIndex, year)}
				style={{
					height: "24px",
					width: "60px",
					borderRadius: "64px",
					color: isSelected ? palette.primary.contrastText : isMonthDisabled ? "gray" : "black",
				}}
				disabled={isMonthDisabled}
			>
				<Typography
					variant="caption"
					style={{
						paddingLeft: spacing(0.2),
						paddingRight: spacing(0.2),
					}}
					className={monthText}
				>
					{firstLetterUppercase(monthLabel)}
				</Typography>
			</IconButton>
		</Box>
	);
};
